import React, { useState } from "react";
import useTranslation from "../../../../shared/hooks/useTranslation";

export type RecurringEditChoice = "thisOnly" | "allFuture";

interface RecurringEditDialogProps {
  open: boolean;
  onConfirm: (choice: RecurringEditChoice) => void;
  onCancel: () => void;
}

/**
 * Confirmation dialog shown when a user Shift+drags a recurring WO event
 * on the calendar. Offers "This occurrence only" vs "This and all future
 * occurrences" radio choice.
 *
 * Uses Tailwind CSS (no MUI Dialog) per project guidelines for new components.
 */
const RecurringEditDialog: React.FC<RecurringEditDialogProps> = ({
  open,
  onConfirm,
  onCancel,
}) => {
  const { translate } = useTranslation("operations");
  const [choice, setChoice] = useState<RecurringEditChoice>("thisOnly");

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[1300] bg-black/20 transition-opacity"
        onClick={onCancel}
      />

      {/* Dialog card */}
      <div
        className="fixed z-[1301] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[360px] max-w-[90vw] bg-white rounded-xl border border-[#E8E5DF] shadow-xl"
        style={{ animation: "recurDialogSlideUp 0.2s ease both" }}
      >
        {/* Header */}
        <div className="px-5 pt-5 pb-3">
          <h3 className="font-sora text-[15px] font-bold text-[#0C1117] leading-tight">
            {translate("recurringEdit.title")}
          </h3>
          <p className="font-sora text-[13px] text-[#8C9099] mt-1 leading-relaxed">
            {translate("recurringEdit.description")}
          </p>
        </div>

        {/* Radio options */}
        <div className="px-5 py-3 flex flex-col gap-2">
          <label
            className={`flex items-center gap-3 px-4 py-3 rounded-lg border cursor-pointer transition-all ${
              choice === "thisOnly"
                ? "border-[#3B82F6] bg-[#3B82F6]/5"
                : "border-[#E8E5DF] hover:border-[#C1C7CD]"
            }`}
            onClick={() => setChoice("thisOnly")}
          >
            <span
              className={`flex-shrink-0 w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors ${
                choice === "thisOnly"
                  ? "border-[#3B82F6]"
                  : "border-[#C1C7CD]"
              }`}
            >
              {choice === "thisOnly" && (
                <span className="w-2 h-2 rounded-full bg-[#3B82F6]" />
              )}
            </span>
            <span className="font-sora text-[13px] font-medium text-[#0C1117]">
              {translate("recurringEdit.thisOnly")}
            </span>
          </label>

          <label
            className={`flex items-center gap-3 px-4 py-3 rounded-lg border cursor-pointer transition-all ${
              choice === "allFuture"
                ? "border-[#3B82F6] bg-[#3B82F6]/5"
                : "border-[#E8E5DF] hover:border-[#C1C7CD]"
            }`}
            onClick={() => setChoice("allFuture")}
          >
            <span
              className={`flex-shrink-0 w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors ${
                choice === "allFuture"
                  ? "border-[#3B82F6]"
                  : "border-[#C1C7CD]"
              }`}
            >
              {choice === "allFuture" && (
                <span className="w-2 h-2 rounded-full bg-[#3B82F6]" />
              )}
            </span>
            <span className="font-sora text-[13px] font-medium text-[#0C1117]">
              {translate("recurringEdit.allFuture")}
            </span>
          </label>
        </div>

        {/* Action buttons */}
        <div className="px-5 pb-5 pt-2 flex justify-end gap-2">
          <button
            onClick={onCancel}
            className="font-sora text-[13px] font-medium text-[#8C9099] px-4 py-2 rounded-lg hover:bg-[#F2F0EB] transition-colors"
          >
            {translate("recurringEdit.cancel")}
          </button>
          <button
            onClick={() => onConfirm(choice)}
            className="font-sora text-[13px] font-semibold text-white bg-[#3B82F6] px-5 py-2 rounded-lg hover:bg-[#2563EB] transition-colors"
          >
            {translate("recurringEdit.continue")}
          </button>
        </div>
      </div>

      {/* Animation keyframe */}
      <style>{`
        @keyframes recurDialogSlideUp {
          from { opacity: 0; transform: translate(-50%, -45%); }
          to   { opacity: 1; transform: translate(-50%, -50%); }
        }
      `}</style>
    </>
  );
};

export default RecurringEditDialog;
